import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Loader2, LogIn, LogOut, Plus, Trash2, Copy, Check, 
  Users, Ticket, RefreshCw, Shield, Download, UserX, UserCheck,
  RotateCcw, AlertTriangle, Database, Activity, Coins, BarChart3,
  Server, FileDown, Zap, Gift, CheckCircle2, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('brain_gym_admin_token'));
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [generateCount, setGenerateCount] = useState(5);
  const [codePrefix, setCodePrefix] = useState('BRAIN');
  const [copiedCode, setCopiedCode] = useState(null);
  const [filterUsed, setFilterUsed] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [prizeClaims, setPrizeClaims] = useState([]);
  const navigate = useNavigate();

  const getApi = useCallback(() => {
    const currentToken = adminToken || localStorage.getItem('brain_gym_admin_token');
    return axios.create({
      baseURL: API,
      headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
    });
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      verifyToken();
    }
  }, []);

  const verifyToken = async () => {
    try {
      await getApi().get('/admin/stats');
      setIsLoggedIn(true);
      fetchAllData();
    } catch {
      localStorage.removeItem('brain_gym_admin_token');
      setAdminToken(null);
      setIsLoggedIn(false);
    }
  };

  const fetchAllData = async (tokenOverride) => {
    setLoading(true);
    try {
      const a = tokenOverride ? axios.create({ baseURL: API, headers: { Authorization: `Bearer ${tokenOverride}` } }) : getApi();
      const [codesRes, statsRes, usersRes, claimsRes] = await Promise.all([
        a.get('/admin/codes'),
        a.get('/admin/stats'),
        a.get('/admin/users'),
        a.get('/admin/prize-claims'),
      ]);
      setCodes(codesRes.data.codes);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPrizeClaims(claimsRes.data.claims);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const res = await getApi().get('/admin/system/health');
      setSystemHealth(res.data);
    } catch {
      toast.error('Failed to fetch system health');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { token } = response.data;
      localStorage.setItem('brain_gym_admin_token', token);
      setAdminToken(token);
      setIsLoggedIn(true);
      toast.success('Admin login successful!');
      fetchAllData(token);
    } catch {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('brain_gym_admin_token');
    setAdminToken(null);
    setIsLoggedIn(false);
    setCodes([]);
    setStats(null);
    setUsers([]);
    toast.success('Logged out');
  };

  const handleGenerateCodes = async () => {
    setLoading(true);
    try {
      const response = await getApi().post('/admin/codes/generate', { count: generateCount, prefix: codePrefix });
      toast.success(`Generated ${response.data.count} new codes!`);
      fetchAllData();
    } catch {
      toast.error('Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      await getApi().delete(`/admin/codes/${code}`);
      toast.success('Code deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete code');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleCopyAllAvailable = () => {
    const availableCodes = codes.filter(c => !c.used).map(c => c.code);
    navigator.clipboard.writeText(availableCodes.join('\n'));
    toast.success(`Copied ${availableCodes.length} codes!`);
  };

  // User management actions
  const handleDeactivateUser = async (userId, name) => {
    if (!window.confirm(`Deactivate ${name}? They won't be able to log in.`)) return;
    try {
      await getApi().post(`/admin/users/${userId}/deactivate`);
      toast.success(`${name} deactivated`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleReactivateUser = async (userId, name) => {
    try {
      await getApi().post(`/admin/users/${userId}/reactivate`);
      toast.success(`${name} reactivated`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`PERMANENTLY delete ${name} and ALL their data? This cannot be undone.`)) return;
    if (!window.confirm(`Are you sure? Type the user name to confirm would be ideal, but clicking OK will proceed.`)) return;
    try {
      const res = await getApi().delete(`/admin/users/${userId}`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetProgress = async (userId, name) => {
    if (!window.confirm(`Reset ALL progress for ${name}? Coins, sessions, streaks will be wiped. Account stays.`)) return;
    try {
      const res = await getApi().post(`/admin/users/${userId}/reset-progress`);
      toast.success(res.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset');
    }
  };

  const handleFulfillPrize = async (milestoneId, userName) => {
    if (!window.confirm(`Mark prize "${milestoneId}" as fulfilled for ${userName}?`)) return;
    try {
      await getApi().post(`/admin/prize-claims/${milestoneId}/${userName}/fulfill`);
      toast.success('Prize marked as fulfilled!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fulfill');
    }
  };


  const handlePurgeAll = async () => {
    if (!window.confirm('NUCLEAR OPTION: Delete ALL data from the entire database?')) return;
    if (!window.confirm('This will delete ALL users, sessions, codes, everything. Are you absolutely sure?')) return;
    try {
      const res = await getApi().post('/admin/system/purge-all');
      toast.success(res.data.message);
      fetchAllData();
    } catch {
      toast.error('Failed to purge');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await getApi().get('/admin/users/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'braingym_users.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported!');
    } catch {
      toast.error('Failed to export');
    }
  };

  const filteredCodes = codes.filter(code => {
    if (filterUsed === 'available') return !code.used;
    if (filterUsed === 'used') return code.used;
    return true;
  });

  const filteredUsers = users.filter(u => {
    const matchesSearch = !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesFilter = userFilter === 'all' || 
      (userFilter === 'active' && !u.deactivated) ||
      (userFilter === 'deactivated' && u.deactivated);
    return matchesSearch && matchesFilter;
  });

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'codes', label: 'Codes', icon: Ticket },
    { id: 'claims', label: 'Prize Claims', icon: Gift },
    { id: 'system', label: 'System', icon: Server },
  ];

  // ============== LOGIN SCREEN ==============
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-slate-800 rounded-2xl flex items-center justify-center mb-4 border border-slate-700">
              <Shield className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white">Admin Console</h1>
            <p className="text-slate-400 mt-2 font-fredoka">Brain Gym Jr. Management</p>
          </div>
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 font-medium">Admin Password</Label>
                <Input
                  id="password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  disabled={loading}
                  data-testid="admin-password-input"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="admin-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-5 h-5" /> Login</>}
              </button>
            </form>
            <div className="mt-6 text-center">
              <button onClick={() => navigate('/login')} className="text-slate-500 hover:text-slate-300 text-sm">
                &larr; Back to App
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== MAIN ADMIN UI ==============
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white font-fredoka">Brain Gym Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchAllData} disabled={loading} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'system') fetchSystemHealth();
                }}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-slate-950 text-emerald-400 border-t-2 border-emerald-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* ============== DASHBOARD TAB ============== */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Top Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} color="blue" label="Total Users" value={stats.users.total} sub={`${stats.users.active} active`} />
              <StatCard icon={Activity} color="emerald" label="Active Today" value={stats.users.active_today} sub={`${stats.users.recent_week} this week`} />
              <StatCard icon={Zap} color="amber" label="Total Sessions" value={stats.sessions.total} />
              <StatCard icon={Ticket} color="purple" label="Codes Available" value={stats.codes.available} sub={`${stats.codes.used} used`} />
            </div>
            {/* Economy Row */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Coins className="w-4 h-4" /> Coin Economy
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-2xl font-bold text-emerald-400">{stats.economy.total_earned.toLocaleString()}</p>
                  <p className="text-sm text-slate-500">Total Earned</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">{stats.economy.total_current.toLocaleString()}</p>
                  <p className="text-sm text-slate-500">In Circulation</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-400">{stats.economy.total_spent.toLocaleString()}</p>
                  <p className="text-sm text-slate-500">Total Spent</p>
                </div>
              </div>
            </div>
            {/* Pool Sizes */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Database className="w-4 h-4" /> Question Pools
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-2xl font-bold text-blue-400">{stats.pools.kids_questions}</p>
                  <p className="text-sm text-slate-500">Kids Pool</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-400">{stats.pools.teen_questions}</p>
                  <p className="text-sm text-slate-500">Teen Pool</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-400">{stats.pools.riddle_history}</p>
                  <p className="text-sm text-slate-500">Riddle History</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============== USERS TAB ============== */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* User toolbar */}
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex items-center gap-3">
                <Input
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-48 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="user-search-input"
                />
                <select
                  value={userFilter}
                  onChange={e => setUserFilter(e.target.value)}
                  className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="user-filter-select"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="deactivated">Deactivated</option>
                </select>
              </div>
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white bg-slate-900 border border-slate-700 px-4 py-2 rounded-lg"
                data-testid="export-csv-btn"
              >
                <FileDown className="w-4 h-4" /> Export CSV
              </button>
            </div>
            {/* User count */}
            <p className="text-sm text-slate-500">{filteredUsers.length} users</p>
            {/* User list */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No users found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800 text-left">
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">Name</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">Age</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Coins</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Sessions</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Streak</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden lg:table-cell">Joined</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">Status</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => (
                        <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-3 px-4">
                            <span className="font-medium text-white">{u.display_name || u.name}</span>
                          </td>
                          <td className="py-3 px-4 text-slate-300">{u.age}</td>
                          <td className="py-3 px-4 text-amber-400 hidden md:table-cell">{u.coins?.toLocaleString()}</td>
                          <td className="py-3 px-4 text-slate-300 hidden md:table-cell">{u.total_sessions}</td>
                          <td className="py-3 px-4 text-slate-300 hidden md:table-cell">{u.streak}/{u.max_streak}</td>
                          <td className="py-3 px-4 text-slate-500 text-sm hidden lg:table-cell">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            {u.deactivated ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-400 border border-red-800">
                                Deactivated
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-800">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-1">
                              {u.deactivated ? (
                                <button
                                  onClick={() => handleReactivateUser(u.id, u.name)}
                                  className="p-1.5 text-emerald-400 hover:bg-emerald-900/30 rounded-lg transition-colors"
                                  title="Reactivate"
                                  data-testid={`reactivate-${u.name}`}
                                >
                                  <UserCheck className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleDeactivateUser(u.id, u.name)}
                                  className="p-1.5 text-amber-400 hover:bg-amber-900/30 rounded-lg transition-colors"
                                  title="Deactivate"
                                  data-testid={`deactivate-${u.name}`}
                                >
                                  <UserX className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleResetProgress(u.id, u.name)}
                                className="p-1.5 text-blue-400 hover:bg-blue-900/30 rounded-lg transition-colors"
                                title="Reset progress"
                                data-testid={`reset-${u.name}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                className="p-1.5 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Delete permanently"
                                data-testid={`delete-user-${u.name}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============== CODES TAB ============== */}
        {activeTab === 'codes' && (
          <div className="space-y-6">
            {/* Generate Section */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-400" /> Generate Enrollment Codes
              </h2>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-sm">Prefix</Label>
                  <Input
                    value={codePrefix}
                    onChange={(e) => setCodePrefix(e.target.value.toUpperCase().slice(0, 10))}
                    placeholder="BRAIN"
                    className="w-32 bg-slate-800 border-slate-700 text-white"
                    data-testid="code-prefix-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-sm">Count</Label>
                  <Input
                    type="number" value={generateCount}
                    onChange={(e) => setGenerateCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                    min="1" max="50"
                    className="w-24 bg-slate-800 border-slate-700 text-white"
                    data-testid="code-count-input"
                  />
                </div>
                <button
                  onClick={handleGenerateCodes} disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 px-6 rounded-xl transition-colors flex items-center gap-2"
                  data-testid="generate-codes-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-5 h-5" /> Generate</>}
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-3">Format: {codePrefix}-XXXX-XXXX</p>
            </div>

            {/* Codes List */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <h2 className="text-base font-bold text-white flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-purple-400" /> Enrollment Codes
                </h2>
                <div className="flex items-center gap-3">
                  <select
                    value={filterUsed} onChange={(e) => setFilterUsed(e.target.value)}
                    className="bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
                    data-testid="filter-select"
                  >
                    <option value="all">All Codes</option>
                    <option value="available">Available</option>
                    <option value="used">Used</option>
                  </select>
                  <button onClick={handleCopyAllAvailable} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm" data-testid="copy-all-btn">
                    <Download className="w-4 h-4" /> Copy Available
                  </button>
                </div>
              </div>

              {filteredCodes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  {filterUsed === 'all' ? 'No codes generated yet' : `No ${filterUsed} codes`}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Code</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">Status</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Created</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Used By</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCodes.map((code) => (
                        <tr key={code.code} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-3 px-4">
                            <span className="font-mono text-white font-medium">{code.code}</span>
                          </td>
                          <td className="py-3 px-4">
                            {code.used ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-900/40 text-orange-400 border border-orange-800">Used</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-800">Available</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm hidden md:table-cell">
                            {new Date(code.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm hidden md:table-cell">
                            {code.used_by_name || '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleCopyCode(code.code)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="Copy" data-testid={`copy-${code.code}`}>
                                {copiedCode === code.code ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                              </button>
                              {!code.used && (
                                <button onClick={() => handleDeleteCode(code.code)} className="p-1.5 text-red-400 hover:bg-red-900/30 rounded-lg" title="Delete" data-testid={`delete-${code.code}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}


        {/* ============== PRIZE CLAIMS TAB ============== */}
        {activeTab === 'claims' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Gift className="w-5 h-5 text-yellow-400" /> Prize Claims
              </h2>
              <p className="text-sm text-slate-500">{prizeClaims.length} total claims</p>
            </div>

            {prizeClaims.length === 0 ? (
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
                <Gift className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">No prize claims yet</p>
                <p className="text-slate-600 text-sm mt-1">Claims will appear here when users reach milestones</p>
              </div>
            ) : (
              <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800 text-left">
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">User</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">Milestone</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Parent Email</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm hidden md:table-cell">Claimed</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm">Status</th>
                        <th className="py-3 px-4 text-slate-400 font-medium text-sm text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prizeClaims.map((claim, idx) => (
                        <tr key={idx} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-3 px-4">
                            <div>
                              <span className="font-medium text-white">{claim.user_name}</span>
                              {claim.user_age && <span className="text-xs text-slate-500 ml-1.5">age {claim.user_age}</span>}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-yellow-400 font-medium text-sm">{claim.milestone_name}</span>
                          </td>
                          <td className="py-3 px-4 hidden md:table-cell">
                            <a href={`mailto:${claim.parent_email}`} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5" />
                              {claim.parent_email}
                            </a>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm hidden md:table-cell">
                            {claim.claimed_at ? new Date(claim.claimed_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            {claim.status === 'fulfilled' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-800">
                                <CheckCircle2 className="w-3 h-3" /> Fulfilled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/40 text-amber-400 border border-amber-800">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {claim.status !== 'fulfilled' && (
                              <button
                                onClick={() => handleFulfillPrize(claim.milestone_id, claim.user_name)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-900/20 hover:bg-emerald-900/40 border border-emerald-800 rounded-lg transition-colors"
                                data-testid={`fulfill-${claim.milestone_id}-${claim.user_name}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Fulfilled
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}


        {/* ============== SYSTEM TAB ============== */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            {/* System Health */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" /> System Health
              </h2>
              {systemHealth ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {Object.entries(systemHealth.collections).map(([name, count]) => (
                    <div key={name} className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                      <p className="text-xs text-slate-400 font-mono truncate">{name}</p>
                      <p className="text-lg font-bold text-white mt-1">{count.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <button onClick={fetchSystemHealth} className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Load system health
                </button>
              )}
            </div>

            {/* Danger Zone */}
            <div className="bg-slate-900 rounded-xl border border-red-900/50 p-5">
              <h2 className="text-base font-bold text-red-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Danger Zone
              </h2>
              <p className="text-sm text-slate-500 mb-4">These actions are irreversible.</p>
              <button
                onClick={handlePurgeAll}
                className="bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800 font-bold py-2.5 px-6 rounded-xl transition-colors flex items-center gap-2"
                data-testid="purge-all-btn"
              >
                <Trash2 className="w-5 h-5" /> Purge All Data
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Reusable stat card component
const StatCard = ({ icon: Icon, color, label, value, sub }) => {
  const colors = {
    blue: 'bg-blue-900/30 text-blue-400 border-blue-800',
    emerald: 'bg-emerald-900/30 text-emerald-400 border-emerald-800',
    amber: 'bg-amber-900/30 text-amber-400 border-amber-800',
    purple: 'bg-purple-900/30 text-purple-400 border-purple-800',
  };
  const iconColors = {
    blue: 'bg-blue-900/50 text-blue-400',
    emerald: 'bg-emerald-900/50 text-emerald-400',
    amber: 'bg-amber-900/50 text-amber-400',
    purple: 'bg-purple-900/50 text-purple-400',
  };
  return (
    <div className={`rounded-xl p-4 border ${colors[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {sub && <p className="text-xs text-slate-600">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
